import { User } from '@client/components/_ui/display/User';
import { useUser } from '@client/hooks/business/user/useUser';
import { trpc } from '@client/trpc/client';
import { SYSTEM_ACCOUNT_IDS } from '@shared/constants';
import { hasPermission } from '@shared/permissions/roles';
import { Divider, Input, Select, type SelectProps, Spin, Switch, Tag, Tooltip } from 'antd';
import { Settings, UserX } from 'lucide-react';
import { type AriaAttributes, type ReactNode, useEffect, useId, useState } from 'react';
import { match } from 'ts-pattern';
import { buildUserOptionGroups, renderMeOption } from './user-selector-options';

type UserSelectorChangeOption = {
    value: bigint | null;
    label: ReactNode;
    /**
     * @deprecated Rewrite logic to use userId or option.value instead
     */
    name: string;
};

type UserSelectorOption = Omit<UserSelectorChangeOption, 'value'> & {
    value: bigint;
    disabled: boolean;
    searchValue: string;
    data: {
        isDisabled: boolean;
    };
};

type Props = {
    /**
     * Forwarded to the underlying Select's input. antd `Form.Item` injects this
     * automatically, wiring its visible label (`htmlFor`) to the combobox so the
     * control gets an accessible name — do not strip it (CUR-704).
     */
    id?: string;
    'aria-label'?: string;
    'aria-labelledby'?: string;
    /**
     * Also injected by antd `Form.Item`: links help/error text, and exposes the
     * required/invalid state to assistive tech. Forwarded for the same reason as `id`.
     */
    'aria-describedby'?: string;
    'aria-invalid'?: AriaAttributes['aria-invalid'];
    'aria-required'?: AriaAttributes['aria-required'];
    value?: bigint | null;
    onChange?: (userId: bigint | null, option: UserSelectorChangeOption) => void;

    readonly?: boolean;
    placeholder?: string;
    className?: string;
    /**
     * If true, only shows users that share handling parties with the current user.
     * handlingPartyIds will work as additional filter in this case
     */
    limitToCurrentUserHandlingParties?: boolean;
    handlingPartyIds?: number[] | bigint[];
    removeSystemAccount?: boolean;
    removeAnonymousAccount?: boolean;
    onlyEmployee?: boolean;
    showDisabledAccounts?: boolean;
    showUnallocated?: boolean;
    /** Show a "Current user" option that resolves to the viewer's id (for saved "My items" filters). */
    showCurrentUser?: boolean;
    /**
     * If true, shows a toggle switch at the top of the dropdown to enable/disable showing disabled users
     */
    showDisabledUsersToggle?: boolean;
    showOtherUser?: boolean;
    onChangeOther?: (value: string | undefined) => void;
    otherValue?: string;
    allowClear?: boolean;
    size?: 'x-small' | 'small' | 'medium' | 'large';
    maxTagCount?: 'responsive' | number;
    getPopupContainer?: SelectProps['getPopupContainer'];
    /**
     * Optional node rendered before each user's avatar (in both the dropdown options and the
     * selected value), e.g. a domain-specific status/pressure indicator. Keeps `UserSelector`
     * domain-agnostic — callers inject the contextual addon.
     */
    renderLabelAddonBefore?: ({ userId }: { userId: bigint }) => ReactNode;
};

const emptyOption: UserSelectorChangeOption = {
    value: null,
    label: '',
    name: '',
};

const isUserSelectorOption = (option: unknown): option is UserSelectorOption =>
    typeof option === 'object' &&
    option !== null &&
    'value' in option &&
    typeof option.value === 'bigint' &&
    'label' in option &&
    'name' in option &&
    typeof option.name === 'string';

export const UserSelector = ({
    id,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledby,
    'aria-describedby': ariaDescribedby,
    'aria-invalid': ariaInvalid,
    'aria-required': ariaRequired,
    value,
    onChange,
    readonly,
    placeholder = 'Select user',
    handlingPartyIds,
    limitToCurrentUserHandlingParties,
    removeSystemAccount = true,
    removeAnonymousAccount = true,
    onlyEmployee,
    showDisabledAccounts = false,
    showUnallocated = false,
    showCurrentUser = false,
    showDisabledUsersToggle = false,
    showOtherUser,
    onChangeOther,
    otherValue,
    className,
    allowClear,
    maxTagCount = 'responsive',
    size = 'medium',
    getPopupContainer,
    renderLabelAddonBefore,
}: Props) => {
    const currentUser = useUser();

    const [showOtherUserInput, setShowOtherUserInput] = useState(value === SYSTEM_ACCOUNT_IDS.OTHER);
    const [includeDisabledUsers, setIncludeDisabledUsers] = useState(showDisabledAccounts);

    const shouldShowDisabledAccounts = showDisabledUsersToggle ? includeDisabledUsers : showDisabledAccounts;

    const { data: options, isPending } = trpc.user.listUsersForSelector.useQuery(
        {
            where: {
                AND: [
                    ...(limitToCurrentUserHandlingParties &&
                    !hasPermission(currentUser.roles, 'org:admin') &&
                    !hasPermission(currentUser.roles, 'super:admin') &&
                    currentUser?.handlingPartyIds?.length
                        ? [
                              {
                                  HandlingParties: {
                                      some: {
                                          handlingPartyId: {
                                              in: currentUser.handlingPartyIds,
                                          },
                                      },
                                  },
                              },
                          ]
                        : []),
                    ...(handlingPartyIds?.length
                        ? [
                              {
                                  HandlingParties: {
                                      some: {
                                          handlingPartyId: {
                                              in: handlingPartyIds,
                                          },
                                      },
                                  },
                              },
                          ]
                        : []),
                    ...(removeSystemAccount
                        ? [
                              {
                                  userId: { not: SYSTEM_ACCOUNT_IDS.SYSTEM },
                                  isSupportAccount: { not: true },
                              },
                          ]
                        : []),
                    // The claims agent (CUR-1158) is never assignable, whatever the caller shows.
                    { userId: { not: SYSTEM_ACCOUNT_IDS.CLARA } },
                    ...(removeAnonymousAccount ? [{ userId: { not: SYSTEM_ACCOUNT_IDS.ANONYMOUS } }] : []),
                    ...(showOtherUser ? [] : [{ userId: { not: SYSTEM_ACCOUNT_IDS.OTHER } }]),
                    ...(onlyEmployee ? [{ isEmployee: true }] : []),
                    // "Soft lock": include the selected disabled user in results only when the value
                    // is currently selected. Disabled users are still unselectable from the dropdown.
                    ...(shouldShowDisabledAccounts
                        ? []
                        : [
                              value
                                  ? {
                                        OR: [{ disabled: false }, { userId: BigInt(value) }],
                                    }
                                  : { disabled: false },
                          ]),
                ],
            },
        },
        {
            select({ rows }) {
                const map = rows.reduce(
                    (prev, item) => {
                        const key = item.fullName.toLowerCase() || item.email;
                        return { ...prev, [key]: prev[key] ? prev[key] + 1 : 1 };
                    },
                    {} as Record<string, number>,
                );

                const options: UserSelectorOption[] = rows.map((item) => {
                    const isOtherUser = item.userId === SYSTEM_ACCOUNT_IDS.OTHER;

                    const key = item.fullName.toLowerCase() || item.email;
                    const name = isOtherUser
                        ? 'Other'
                        : map[key] > 1
                          ? `${item.fullName} (${item.email})`
                          : item.fullName;

                    return {
                        value: item.userId,
                        name,
                        disabled: !showDisabledUsersToggle && item.disabled,
                        label: (
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    {renderLabelAddonBefore?.({ userId: item.userId })}
                                    <User
                                        userId={item.userId}
                                        photoUrl={item.photoUrl}
                                        name={name}
                                        showPopover={false}
                                        size={size}
                                    />
                                </div>
                                {item.disabled && (
                                    <Tooltip
                                        title={
                                            showDisabledUsersToggle
                                                ? 'Disabled user'
                                                : 'This user is currently disabled and cannot be added to new assignments, but can be removed from existing ones.'
                                        }
                                        placement="left"
                                    >
                                        <UserX className="ml-2 size-3.5 text-subtle-foreground" />
                                    </Tooltip>
                                )}
                            </div>
                        ),
                        searchValue: `${isOtherUser ? 'other' : item.fullName} ${item.email} ${item.phone || ''}`
                            .trim()
                            .toLowerCase(),
                        data: {
                            isDisabled: item.disabled,
                        },
                    };
                });

                if (showUnallocated) {
                    options.unshift({
                        value: SYSTEM_ACCOUNT_IDS.UNALLOCATED,
                        name: 'Unallocated',
                        disabled: false,
                        label: (
                            <div className="flex items-center justify-between">
                                <User size={size} />
                            </div>
                        ),
                        searchValue: 'unallocated',
                        data: {
                            isDisabled: false,
                        },
                    });
                }

                if (showCurrentUser) {
                    options.unshift({
                        value: SYSTEM_ACCOUNT_IDS.CURRENT_USER,
                        name: 'Me',
                        disabled: false,
                        // Dynamic "Me" sentinel — a two-line card under the "Relative to you" group
                        // (NOT the viewer's own avatar, so it can't be mistaken for their named row,
                        // which still appears under "People").
                        label: renderMeOption(currentUser.fullName),
                        searchValue: 'me current user',
                        data: {
                            isDisabled: false,
                        },
                    });
                }

                return options;
            },
        },
    );

    useEffect(() => {
        if (options && value && !options.find((item) => Number(item.value) === Number(value))) {
            onChange?.(null, emptyOption);
        }
    }, [onChange, options, value]);

    const isLoading = isPending || !options;
    const isSelectedUserDisabled =
        options?.find((item) => Number(item.value) === Number(value))?.data.isDisabled === true;
    // Keep `options` flat for the value/label lookups above and below; group only the rendered list.
    const groupedOptions = buildUserOptionGroups(options, showCurrentUser);

    const disabledUserWarning =
        isSelectedUserDisabled && !showDisabledUsersToggle
            ? 'This user is currently disabled and cannot be added to new assignments, but can be removed from existing ones.'
            : undefined;
    // The visual warning Tooltip targets the wrapper div, NEVER the Select: antd
    // Tooltip clones its child with its own aria-describedby (undefined while closed),
    // which would stomp the Form.Item-injected help/error id. The wrapper div is
    // always mounted so the tree shape — and therefore the Select's focus — is stable
    // whether or not the warning is active (CUR-704 rounds 2–3). Assistive tech gets
    // the warning through a stable hidden node merged into the combobox description.
    const warningId = useId();
    const describedBy =
        [disabledUserWarning ? warningId : undefined, ariaDescribedby].filter(Boolean).join(' ') || undefined;

    const select = (
        <Select
            id={id}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledby}
            aria-describedby={describedBy}
            aria-invalid={ariaInvalid}
            aria-required={ariaRequired}
            value={value}
            onChange={(nextValue, option) => {
                if (showOtherUser) {
                    setShowOtherUserInput(nextValue === SYSTEM_ACCOUNT_IDS.OTHER);
                }

                // Handle clear button - value will be undefined
                if (nextValue === undefined) {
                    onChange?.(null, emptyOption);
                } else {
                    const nextOption = Array.isArray(option) ? option[0] : option;

                    onChange?.(
                        nextValue,
                        isUserSelectorOption(nextOption)
                            ? {
                                  value: nextOption.value,
                                  label: nextOption.label,
                                  name: nextOption.name,
                              }
                            : emptyOption,
                    );
                }
            }}
            options={groupedOptions}
            loading={isPending}
            placeholder={placeholder}
            suffixIcon={null}
            showSearch={{
                filterOption: (input, option) => !!option?.searchValue?.includes(input.toLowerCase()),
            }}
            disabled={readonly}
            labelRender={(label) => {
                if (isLoading) {
                    return (
                        <Tag className="flex items-center gap-2 bg-transparent text-subtle-foreground">
                            <Spin size="small" />
                            Loading users...
                        </Tag>
                    );
                }
                // The "Me" sentinel reads as clean text once selected (its accent icon-circle
                // lives in the dropdown option) — matching UserSelectorMultiple and the "Mine" tag.
                if (Number(label.value) === Number(SYSTEM_ACCOUNT_IDS.CURRENT_USER)) {
                    return 'Me';
                }
                return options?.find((item) => Number(item.value) === Number(label.value))?.label || '';
            }}
            popupMatchSelectWidth={false}
            popupClassName={showCurrentUser ? 'crm-grouped-filter-popup' : undefined}
            getPopupContainer={getPopupContainer}
            allowClear={allowClear}
            popupRender={(menu: ReactNode) => (
                <div>
                    {showDisabledUsersToggle && (
                        <>
                            <div className="flex items-center justify-between bg-muted px-3 py-2">
                                <div className="flex items-center gap-2">
                                    <Settings size={14} className="text-subtle-foreground" />
                                    <span className="text-muted-foreground text-sm">Show disabled users</span>
                                </div>
                                <Switch
                                    size="small"
                                    checked={includeDisabledUsers}
                                    onChange={setIncludeDisabledUsers}
                                />
                            </div>
                            <Divider className="m-0" />
                        </>
                    )}
                    {menu}
                </div>
            )}
            maxTagCount={maxTagCount}
            className={className}
            size={match(size)
                .with('x-small', 'small', () => 'small' as const)
                .with('medium', () => 'middle' as const)
                .with('large', () => 'large' as const)
                .exhaustive()}
        />
    );

    return (
        <>
            <Tooltip title={disabledUserWarning}>
                {/* A stable, measurable trigger keeps the warning positioned without letting
                    Tooltip clone over the Select's aria-describedby. */}
                <div className="inline-block w-full">{select}</div>
            </Tooltip>
            {disabledUserWarning && (
                <span id={warningId} className="sr-only">
                    {disabledUserWarning}
                </span>
            )}
            {showOtherUserInput && (
                <Input className="mt-2" value={otherValue || ''} onChange={(e) => onChangeOther?.(e.target.value)} />
            )}
        </>
    );
};
